import React from 'react';
import { IonSkeletonText } from '@ionic/react';

export type ProLoadingListVariant = 'card' | 'list-row' | 'profile' | 'notification' | 'calendar-day' | 'lines';

export interface ProLoadingListProps {
  variant?: ProLoadingListVariant;
  count?: number;
  className?: string;
}

const CardSkeleton: React.FC = () => (
  <div className="rounded-xl bg-white dark:bg-background-dark-secondary border border-border-light dark:border-white/10 p-4 space-y-2">
    <IonSkeletonText animated style={{ width: '40%', height: '16px' }} />
    <IonSkeletonText animated style={{ width: '90%', height: '14px' }} />
    <IonSkeletonText animated style={{ width: '75%', height: '14px' }} />
    <IonSkeletonText animated style={{ width: '55%', height: '12px' }} />
  </div>
);

const ListRowSkeleton: React.FC = () => (
  <div className="flex items-center gap-3 py-3">
    <div className="w-10 h-10 rounded-full overflow-hidden shrink-0">
      <IonSkeletonText animated style={{ width: '100%', height: '100%' }} />
    </div>
    <div className="flex-1 space-y-1">
      <IonSkeletonText animated style={{ width: '55%', height: '14px' }} />
      <IonSkeletonText animated style={{ width: '75%', height: '12px' }} />
    </div>
  </div>
);

const ProfileSkeleton: React.FC = () => (
  <>
    <IonSkeletonText animated style={{ width: '160px', height: '20px' }} />
    <IonSkeletonText animated style={{ width: '200px', height: '16px', marginTop: '4px' }} />
  </>
);

const NotificationSkeleton: React.FC = () => (
  <div className="flex items-start gap-3 py-3">
    <IonSkeletonText animated style={{ width: '24px', height: '24px', borderRadius: '50%' }} />
    <div className="flex-1 space-y-1">
      <IonSkeletonText animated style={{ width: '70%', height: '16px' }} />
      <IonSkeletonText animated style={{ width: '90%', height: '14px' }} />
    </div>
  </div>
);

const CalendarDaySkeleton: React.FC = () => (
  <div className="mb-4">
    <IonSkeletonText animated style={{ width: '35%', height: '14px', marginBottom: '8px' }} />
    <div className="rounded-lg border border-border-light dark:border-white/10 p-3 space-y-2">
      <div className="flex justify-between">
        <IonSkeletonText animated style={{ width: '60%', height: '14px' }} />
        <IonSkeletonText animated style={{ width: '15%', height: '14px' }} />
      </div>
      <IonSkeletonText animated style={{ width: '45%', height: '12px' }} />
      <IonSkeletonText animated style={{ width: '30%', height: '12px' }} />
    </div>
  </div>
);

const LinesSkeleton: React.FC = () => (
  <IonSkeletonText animated style={{ width: '100%', height: '20px' }} />
);

const VARIANT_COMPONENT: Record<ProLoadingListVariant, React.FC> = {
  card: CardSkeleton,
  'list-row': ListRowSkeleton,
  profile: ProfileSkeleton,
  notification: NotificationSkeleton,
  'calendar-day': CalendarDaySkeleton,
  lines: LinesSkeleton,
};

/**
 * Standard IonSkeletonText loading blocks for Pro mobile lists.
 */
const ProLoadingList: React.FC<ProLoadingListProps> = ({
  variant = 'card',
  count = 4,
  className = '',
}) => {
  const Item = VARIANT_COMPONENT[variant];
  return (
    <div className={className}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i}>
          <Item />
        </div>
      ))}
    </div>
  );
};

export default ProLoadingList;
